import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { ArrowLeft, Clock, Save, Building2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';
import ImageUploader from '../components/ImageUploader';

const PropertySettings = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    nom: '',
    adresse: '',
    description: '',
    image_url: '',
    actif: true,
    default_checkin_time: '16:00',
  });

  useEffect(() => {
    fetchProperty();
  }, [id]);

  const fetchProperty = async () => {
    try {
      const response = await api.get(`/properties/${id}`);
      const prop = response.data;
      setProperty(prop);
      setFormData({
        nom: prop.nom || '',
        adresse: prop.adresse || '',
        description: prop.description || '',
        image_url: prop.image_url || '',
        actif: prop.actif ?? true,
        default_checkin_time: prop.default_checkin_time || '16:00',
      });
    } catch (error) {
      toast.error('Erreur lors du chargement du logement');
      navigate('/properties');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put(`/properties/${id}`, formData);
      toast.success('Logement mis à jour avec succès !');
      navigate('/properties');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50/50 flex items-center justify-center">
        <div className="text-slate-600">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50/50 p-4 sm:p-6 lg:p-8">
      <div className="max-w-2xl mx-auto">
        <Button
          variant="ghost"
          onClick={() => navigate('/properties')}
          data-testid="back-to-properties-btn"
          className="mb-4 sm:mb-6 text-slate-600 hover:text-slate-900"
        >
          <ArrowLeft className="w-4 h-4 mr-2" strokeWidth={1.5} />
          Retour aux logements
        </Button>

        <div className="mb-6">
          <h1 className="text-xl sm:text-2xl lg:text-3xl font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Paramètres du logement
          </h1>
          <p className="text-sm sm:text-base text-slate-600 mt-1">{property?.nom}</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Informations générales */}
          <Card className="bg-white border border-slate-100 shadow-sm rounded-xl p-4 sm:p-6">
            <div className="flex items-center gap-2 mb-4">
              <Building2 className="w-5 h-5 text-slate-700" strokeWidth={1.5} />
              <h2 className="text-lg font-medium text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Informations générales
              </h2>
            </div>
            
            <div className="space-y-4">
              <div>
                <Label htmlFor="nom">Nom du logement</Label>
                <Input
                  id="nom"
                  value={formData.nom}
                  onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                  required
                  data-testid="settings-nom-input"
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label htmlFor="adresse">Adresse</Label>
                <Input
                  id="adresse"
                  value={formData.adresse}
                  onChange={(e) => setFormData({ ...formData, adresse: e.target.value })}
                  required
                  data-testid="settings-adresse-input"
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  required
                  data-testid="settings-description-input"
                  className="mt-1"
                  rows={3}
                />
              </div>
              
              <div>
                <ImageUploader
                  value={formData.image_url}
                  onChange={(url) => setFormData({ ...formData, image_url: url })}
                  label="Image du logement"
                />
              </div>
              
              <div className="flex items-center space-x-2">
                <Switch
                  id="actif"
                  checked={formData.actif}
                  onCheckedChange={(checked) => setFormData({ ...formData, actif: checked })}
                  data-testid="settings-actif-switch"
                />
                <Label htmlFor="actif">Logement actif</Label>
              </div>
            </div>
          </Card>

          {/* Paramètres de check-in */}
          <Card className="bg-white border border-slate-100 shadow-sm rounded-xl p-4 sm:p-6">
            <div className="flex items-center gap-2 mb-4">
              <Clock className="w-5 h-5 text-rose-600" strokeWidth={1.5} />
              <h2 className="text-lg font-medium text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Heure de check-in par défaut
              </h2>
            </div>
            
            <div className="space-y-4">
              <div>
                <Label htmlFor="default_checkin_time" className="text-sm text-slate-700">
                  Heure d'arrivée standard des voyageurs
                </Label>
                <Input
                  id="default_checkin_time"
                  type="time"
                  value={formData.default_checkin_time}
                  onChange={(e) => setFormData({ ...formData, default_checkin_time: e.target.value })}
                  data-testid="settings-checkin-time-input"
                  className="mt-1 w-40"
                />
                <p className="text-xs text-slate-500 mt-2">
                  Cette heure est utilisée pour calculer les délais d'achat des options. 
                  Par exemple, si une option est disponible jusqu'à 24h avant l'arrivée et que le check-in est à 16h00, 
                  l'option sera disponible jusqu'à 16h00 la veille.
                </p>
              </div>
            </div>
          </Card>

          {/* Bouton de sauvegarde */}
          <div className="flex justify-end">
            <Button
              type="submit"
              disabled={saving}
              data-testid="settings-save-btn"
              className="bg-rose-600 hover:bg-rose-700 text-white rounded-lg px-6"
            >
              {saving ? (
                <>Enregistrement...</>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" strokeWidth={1.5} />
                  Enregistrer
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PropertySettings;
